import { AlertTriangle, Check, Plus, Trash2 } from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useReducer, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { Level3Data, Level3LineItem } from "@tilt/pos-sdk";

import { cn } from "../../lib/utils";

export interface Level3FormHandle {
  validate: () => boolean;
  getSanitized: () => Level3Data;
}

interface FormState {
  zip_code: string;
  tax_amount_str: string;
  freight_amount_str: string;
  duty_amount_str: string;
  line_items: Array<{
    description: string;
    quantity_str: string;
    unit_price_str: string;
    product_code: string;
    commodity_code: string;
    unit_of_measure: string;
  }>;
}

type Action =
  | { type: "SET_ZIP"; value: string }
  | { type: "SET_TAX"; value: string }
  | { type: "SET_FREIGHT"; value: string }
  | { type: "SET_DUTY"; value: string }
  | { type: "SET_ITEM"; idx: number; field: keyof FormState["line_items"][0]; value: string }
  | { type: "ADD_ITEM" }
  | { type: "REMOVE_ITEM"; idx: number }
  | { type: "SET_UNIT_PRICE_AUTO"; idx: number; value: string };

function reducer(state: FormState, action: Action): FormState {
  switch (action.type) {
    case "SET_ZIP":
      return { ...state, zip_code: action.value };
    case "SET_TAX":
      return { ...state, tax_amount_str: action.value };
    case "SET_FREIGHT":
      return { ...state, freight_amount_str: action.value };
    case "SET_DUTY":
      return { ...state, duty_amount_str: action.value };
    case "SET_ITEM": {
      const items = state.line_items.map((item, i) =>
        i === action.idx ? { ...item, [action.field]: action.value } : item,
      );
      return { ...state, line_items: items };
    }
    case "ADD_ITEM":
      return {
        ...state,
        line_items: [
          ...state.line_items,
          {
            description: "",
            quantity_str: "1",
            unit_price_str: "0.00",
            product_code: "",
            commodity_code: "",
            unit_of_measure: "",
          },
        ],
      };
    case "REMOVE_ITEM":
      return {
        ...state,
        line_items: state.line_items.filter((_, i) => i !== action.idx),
      };
    case "SET_UNIT_PRICE_AUTO":
      return {
        ...state,
        line_items: state.line_items.map((item, i) =>
          i === action.idx ? { ...item, unit_price_str: action.value } : item,
        ),
      };
    default:
      return state;
  }
}

function parseCents(str: string): number {
  const v = parseFloat(str);
  return isNaN(v) ? 0 : Math.round(v * 100);
}

function centsToStr(cents: number): string {
  return (cents / 100).toFixed(2);
}

function computeLineTotal(state: FormState): number {
  return state.line_items.reduce((sum, item) => {
    const qty = parseFloat(item.quantity_str) || 0;
    const up = parseCents(item.unit_price_str);
    return sum + Math.round(qty * up);
  }, 0);
}

interface Props {
  /** Total payment amount in cents - line_items + tax must equal this. */
  readonly amountCents: number;
  readonly initialZip?: string;
  /** Fires whenever the balance validity changes (true = balanced). */
  readonly onChange?: (balanced: boolean) => void;
  /** Extra classes for the outer container - use to strip border/padding
   *  when embedding inside another panel (e.g. VirtualTerminalPanel). */
  readonly className?: string;
}

export const Level3Form = forwardRef<Level3FormHandle, Props>(function Level3Form(
  { amountCents, initialZip = "", onChange, className },
  ref,
) {
  const { t } = useTranslation();

  const [state, dispatch] = useReducer(reducer, {
    zip_code: initialZip,
    tax_amount_str: "0.00",
    freight_amount_str: "",
    duty_amount_str: "",
    line_items: [
      {
        description: "",
        quantity_str: "1",
        unit_price_str: centsToStr(amountCents),
        product_code: "",
        commodity_code: "",
        unit_of_measure: "",
      },
    ],
  });

  // Auto-balance single line item: when exactly one line item exists,
  // reactively set unit_price = (amountTarget - tax) / qty.
  // Mirrors V1's syncSingleLineToTarget() in Level3Form.vue:142-150.
  const taxCents = parseCents(state.tax_amount_str);
  const firstQtyStr = state.line_items.length === 1 ? state.line_items[0].quantity_str : "";
  const prevAutoRef = useRef<{ tax: number; amount: number } | null>(null);
  useEffect(() => {
    if (state.line_items.length !== 1) return;
    const qty = parseFloat(firstQtyStr) || 1;
    const newPrice = Math.round((amountCents - taxCents) / qty);
    const prev = prevAutoRef.current;
    if (prev && prev.tax === taxCents && prev.amount === amountCents) return;
    prevAutoRef.current = { tax: taxCents, amount: amountCents };
    dispatch({
      type: "SET_UNIT_PRICE_AUTO",
      idx: 0,
      value: centsToStr(Math.max(0, newPrice)),
    });
  }, [amountCents, taxCents, state.line_items.length, firstQtyStr]);

  const lineTotal = computeLineTotal(state);
  const totalWithTax = lineTotal + taxCents;
  const diffCents = totalWithTax - amountCents;
  const balanced = diffCents === 0;

  // Required fields: zip + non-empty description + positive qty on every line.
  const requiredFilled =
    state.zip_code.trim().length > 0 &&
    state.line_items.length > 0 &&
    state.line_items.every(
      (item) => item.description.trim().length > 0 && (parseFloat(item.quantity_str) || 0) > 0,
    );

  const valid = balanced && requiredFilled;

  useEffect(() => {
    onChange?.(valid);
  }, [valid, onChange]);

  useImperativeHandle(ref, () => ({
    validate() {
      if (!state.zip_code.trim()) return false;
      if (state.line_items.length === 0) return false;
      for (const item of state.line_items) {
        if (!item.description.trim()) return false;
        if ((parseFloat(item.quantity_str) || 0) <= 0) return false;
        if (parseCents(item.unit_price_str) < 0) return false;
      }
      return balanced;
    },
    getSanitized(): Level3Data {
      const items: Level3LineItem[] = state.line_items.map((item) => ({
        description: item.description.trim(),
        quantity: parseFloat(item.quantity_str) || 1,
        unit_price_cents: parseCents(item.unit_price_str),
        ...(item.product_code.trim() ? { product_code: item.product_code.trim() } : {}),
        ...(item.commodity_code.trim() ? { commodity_code: item.commodity_code.trim() } : {}),
        ...(item.unit_of_measure.trim() ? { unit_of_measure: item.unit_of_measure.trim() } : {}),
      }));
      const out: Level3Data = {
        zip_code: state.zip_code.trim(),
        tax_amount_cents: taxCents,
        line_items: items,
      };
      const freight = parseCents(state.freight_amount_str);
      const duty = parseCents(state.duty_amount_str);
      if (state.freight_amount_str.trim()) Object.assign(out, { freight_amount_cents: freight });
      if (state.duty_amount_str.trim()) Object.assign(out, { duty_amount_cents: duty });
      return out;
    },
  }));

  return (
    <div className={cn("space-y-3 rounded-lg border border-line bg-card p-3 text-sm", className)}>
      <p className="font-medium text-strong">{t("sale.level3.title")}</p>

      {/* ZIP + Tax */}
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted">
            {t("sale.level3.zip")} <span className="text-rose-500">*</span>
          </span>
          <input
            type="text"
            inputMode="numeric"
            maxLength={10}
            value={state.zip_code}
            onChange={(e) => dispatch({ type: "SET_ZIP", value: e.target.value })}
            className="rounded-md border border-line bg-surface px-2 py-1.5 text-foreground ring-0 focus:outline-none focus:ring-2 focus:ring-brand-primary"
            placeholder="12345"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted">{t("sale.level3.tax")}</span>
          <input
            type="text"
            inputMode="decimal"
            value={state.tax_amount_str}
            onChange={(e) => dispatch({ type: "SET_TAX", value: e.target.value })}
            className="rounded-md border border-line bg-surface px-2 py-1.5 text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary"
            placeholder="0.00"
          />
        </label>
      </div>

      {/* Line items */}
      <div className="space-y-2">
        <p className="text-xs text-muted">{t("sale.level3.lineItems")}</p>
        {state.line_items.map((item, idx) => (
          <div key={idx} className="rounded-md border border-line bg-surface p-2 space-y-1.5">
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] text-muted">
                {t("sale.level3.description")} <span className="text-rose-500">*</span>
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  maxLength={40}
                  value={item.description}
                  onChange={(e) =>
                    dispatch({ type: "SET_ITEM", idx, field: "description", value: e.target.value })
                  }
                  placeholder={t("sale.level3.description")}
                  className="flex-1 rounded border border-line bg-card px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-brand-primary"
                />
                {state.line_items.length > 1 && (
                  <button
                    type="button"
                    aria-label={t("sale.level3.removeLine")}
                    onClick={() => dispatch({ type: "REMOVE_ITEM", idx })}
                    className="rounded p-0.5 text-muted hover:text-rose-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] text-muted">{t("sale.level3.qty")}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={item.quantity_str}
                  onChange={(e) =>
                    dispatch({
                      type: "SET_ITEM",
                      idx,
                      field: "quantity_str",
                      value: e.target.value,
                    })
                  }
                  className="rounded border border-line bg-card px-1.5 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-brand-primary"
                />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] text-muted">{t("sale.level3.unitPrice")}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={item.unit_price_str}
                  onChange={(e) =>
                    dispatch({
                      type: "SET_ITEM",
                      idx,
                      field: "unit_price_str",
                      value: e.target.value,
                    })
                  }
                  className="rounded border border-line bg-card px-1.5 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-brand-primary"
                />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] text-muted">{t("sale.level3.productCode")}</span>
                <input
                  type="text"
                  maxLength={12}
                  value={item.product_code}
                  onChange={(e) =>
                    dispatch({
                      type: "SET_ITEM",
                      idx,
                      field: "product_code",
                      value: e.target.value,
                    })
                  }
                  className="rounded border border-line bg-card px-1.5 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-brand-primary"
                />
              </label>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => dispatch({ type: "ADD_ITEM" })}
          className="flex items-center gap-1 text-xs text-brand-primary hover:underline"
        >
          <Plus className="h-3 w-3" />
          {t("sale.level3.addLine")}
        </button>
      </div>

      {/* Balance indicator */}
      <div
        className={cn(
          "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium",
          balanced
            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
            : "bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
        )}
        role="status"
        aria-live="polite"
      >
        {balanced ? (
          <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
        ) : (
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
        )}
        {balanced
          ? t("sale.level3.balanced")
          : t("sale.level3.unbalanced", {
              diff: (Math.abs(diffCents) / 100).toFixed(2),
              sign: diffCents > 0 ? "+" : "−",
            })}
      </div>
    </div>
  );
});
